import { FormEvent, useEffect, useState } from 'react'
import { fetchAnnualOkrs, fetchGoals, fetchSettings, fetchWorkLogs, saveSettings } from '../utils/api'
import type { SettingsData } from '../types/okr'

function Settings() {
  const [settings, setSettings] = useState<SettingsData | null>(null)
  const [counts, setCounts] = useState({ okrs: 0, goals: 0, worklogs: 0 })
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    const load = async () => {
      try {
        const [settingsData, annual, goals, logs] = await Promise.all([
          fetchSettings(),
          fetchAnnualOkrs(),
          fetchGoals(),
          fetchWorkLogs()
        ])
        setSettings(settingsData)
        setCounts({ okrs: annual.okrs.length, goals: goals.length, worklogs: logs.length })
      } catch (err) {
        setError(err instanceof Error ? err.message : '加载设置失败')
      }
    }

    load()
  }, [])

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!settings) return
    setSaving(true)
    setMessage('')
    setError('')

    try {
      const payload = { ...settings, updatedAt: new Date().toISOString() }
      await saveSettings(payload)
      setSettings(payload)
      setMessage('系统设置已保存')
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败')
    } finally {
      setSaving(false)
    }
  }

  if (!settings) {
    return <div className="page-shell"><div className="empty-state">加载系统设置中...</div></div>
  }

  return (
    <div className="page-shell">
      <section className="page-header-block">
        <div>
          <span className="section-kicker">系统信息</span>
          <h2>系统设置</h2>
          <p>这里集中维护当前用户信息、企微接入参数、汇报偏好与数据来源说明。</p>
        </div>
        <div className="page-header-actions">
          {message && <span className="success-text">{message}</span>}
          {error && <span className="error-text">{error}</span>}
        </div>
      </section>

      <section className="stats-grid compact-stats">
        <article className="stat-card-modern"><span>年度OKR</span><strong>{counts.okrs}</strong></article>
        <article className="stat-card-modern"><span>执行目标</span><strong>{counts.goals}</strong></article>
        <article className="stat-card-modern"><span>工作日志</span><strong>{counts.worklogs}</strong></article>
      </section>

      <form className="card-panel form-panel" onSubmit={submit}>
        <div className="panel-header"><h3>基础配置</h3></div>
        <div className="form-grid">
          <label>
            <span>姓名</span>
            <input value={settings.profile.name} onChange={(e) => setSettings({ ...settings, profile: { ...settings.profile, name: e.target.value } })} />
          </label>
          <label>
            <span>用户ID</span>
            <input value={settings.profile.userId} onChange={(e) => setSettings({ ...settings, profile: { ...settings.profile, userId: e.target.value } })} />
          </label>
          <label>
            <span>部门</span>
            <input value={settings.profile.department} onChange={(e) => setSettings({ ...settings, profile: { ...settings.profile, department: e.target.value } })} />
          </label>
          <label>
            <span>角色</span>
            <input value={settings.profile.role} onChange={(e) => setSettings({ ...settings, profile: { ...settings.profile, role: e.target.value } })} />
          </label>
          <label>
            <span>应用名称</span>
            <input value={settings.system.appName} onChange={(e) => setSettings({ ...settings, system: { ...settings.system, appName: e.target.value } })} />
          </label>
          <label>
            <span>CorpId</span>
            <input value={settings.system.corpId} onChange={(e) => setSettings({ ...settings, system: { ...settings.system, corpId: e.target.value } })} />
          </label>
          <label>
            <span>AgentId</span>
            <input value={settings.system.agentId} onChange={(e) => setSettings({ ...settings, system: { ...settings.system, agentId: e.target.value } })} />
          </label>
          <label>
            <span>应用主页</span>
            <input value={settings.system.homepage} onChange={(e) => setSettings({ ...settings, system: { ...settings.system, homepage: e.target.value } })} />
          </label>
          <label className="full-span">
            <span>回调地址</span>
            <input value={settings.system.callbackUrl} onChange={(e) => setSettings({ ...settings, system: { ...settings.system, callbackUrl: e.target.value } })} />
          </label>
          <label>
            <span>汇报版本偏好</span>
            <input value={settings.reporting.preferredVersion} onChange={(e) => setSettings({ ...settings, reporting: { ...settings.reporting, preferredVersion: e.target.value } })} />
          </label>
          <label>
            <span>汇报风格</span>
            <input value={settings.reporting.style} onChange={(e) => setSettings({ ...settings, reporting: { ...settings.reporting, style: e.target.value } })} />
          </label>
          <label className="full-span">
            <span>数据来源（每行一条）</span>
            <textarea rows={4} value={settings.sources.join('\n')} onChange={(e) => setSettings({ ...settings, sources: e.target.value.split('\n').map((item) => item.trim()).filter(Boolean) })} />
          </label>
          <label className="full-span">
            <span>备注（每行一条）</span>
            <textarea rows={4} value={settings.notes.join('\n')} onChange={(e) => setSettings({ ...settings, notes: e.target.value.split('\n').map((item) => item.trim()).filter(Boolean) })} />
          </label>
        </div>
        <button className="primary-btn" disabled={saving}>{saving ? '保存中...' : '保存系统设置'}</button>
      </form>
    </div>
  )
}

export default Settings
